import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Button from "@mui/material/Button";
import { Formik, Form, getIn } from "formik";
import * as Yup from "yup";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectUser } from "../redux/modules/userSlice";
import { DownloadFormHtmlToPdf, Form11API } from "../components/api";
import useDraft from "../hooks/useDraft";
import {
  applySectionDraftData,
  pickSectionDraftData,
} from "../drafts/draftHelpers";
import {
  JOINEE_FORM_COLORS,
  JOINEE_FORM_FONT,
} from "../config/joineeFormConstants";
import JoineeFormShell, {
  JoineeFormFooterActions,
} from "../components/NewJoineForm/JoineeFormShell";
import {
  FormSectionTitle,
  FormTextField,
  FormDateField,
  FormTwoCol,
  FormCheckboxField,
  FormRadioField,
} from "../components/NewJoineForm/JoineeFormFields";
import ApprovedCustomizedDialogs from "../components/Model/ModelApproved";
import {
  canShowHrApprove,
  canShowHrResubmit,
  canShowHrReviewActions,
  isJoineeEmployeeFormLocked,
} from "../config/joineeConstants";
import ReSubmitCustomizedDialogs from "../components/Model/ModelReSubmit";
import LoadingButtons from "../components/Model/LoadingButtonModel";
import HrResubmitNotice from "../components/Joinee/HrResubmitNotice";
import { preferSavedDate, isFutureDate } from "../utils/dateHelpers";
import { ruleSchema, validateDraft, notifyFormInvalid } from "../utils/inputRules";

var EMPTY_DISPLAY = "—";
var YES_NO_OPTIONS = [
  { value: "Yes", label: "Yes" },
  { value: "No", label: "No" },
];
var IFSC_REGEX = /^[A-Z]{4}0[A-Z0-9]{6}$/;
var ACCOUNT_REGEX = /^\d{9,18}$/;

function errMsg(formik, path) {
  var touched = getIn(formik.touched, path);
  var error = getIn(formik.errors, path);
  return touched && error ? error : "";
}

function displayValue(value) {
  if (value === null || value === undefined) {
    return EMPTY_DISPLAY;
  }
  var text = String(value).trim();
  return text ? text : EMPTY_DISPLAY;
}

function formatDob(value) {
  if (!value) {
    return EMPTY_DISPLAY;
  }
  var d = dayjs(value);
  return d.isValid() ? d.format("DD MMM YYYY") : displayValue(value);
}

function formatMarital(value) {
  if (!value) {
    return EMPTY_DISPLAY;
  }
  var v = String(value).toLowerCase();
  if (v === "married") return "Married";
  if (v === "single" || v === "unmarried") return "Unmarried";
  if (v === "widow") return "Widow";
  if (v === "widower") return "Widower";
  if (v === "divorcee") return "Divorcee";
  return displayValue(value);
}

function formatGender(value) {
  if (!value) {
    return EMPTY_DISPLAY;
  }
  var v = String(value).toLowerCase();
  if (v === "male") return "Male";
  if (v === "female") return "Female";
  if (v === "prefer not to answer") return "Prefer not to answer";
  return displayValue(value);
}

function formatAadhaar(value) {
  if (!value) {
    return EMPTY_DISPLAY;
  }
  var digits = String(value).replace(/\D/g, "");
  if (!digits) {
    return displayValue(value);
  }
  var parts = [];
  for (var i = 0; i < digits.length; i += 4) {
    parts.push(digits.slice(i, i + 4));
  }
  return parts.join(" ");
}

function safeFatherName(userResp) {
  if (!userResp || !userResp.father) {
    return "";
  }
  if (typeof userResp.father === "string") {
    return userResp.father;
  }
  return userResp.father.name || "";
}

function resolveRelative(userResp, form11Details) {
  var fatherName = safeFatherName(userResp);
  var spouseName =
    userResp && userResp.spouse ? String(userResp.spouse).trim() : "";
  var savedRel =
    form11Details && form11Details.relationship
      ? String(form11Details.relationship).toLowerCase()
      : "";
  var marital =
    userResp && userResp.maritalStatus
      ? String(userResp.maritalStatus).toLowerCase()
      : "";
  var useSpouse =
    savedRel === "spouse" || (marital === "married" && Boolean(spouseName));
  return {
    relationship: useSpouse ? "spouse" : "father",
    relativeName: useSpouse && spouseName ? spouseName : fatherName,
  };
}

function digitsOnly(value) {
  return String(value || "").replace(/\D/g, "");
}

function normalizeIfsc(value) {
  return String(value || "")
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "")
    .slice(0, 11);
}

function ReadOnlyRow(props) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        gap: { xs: "16px", sm: "32px" },
        width: "100%",
        mb: 2,
      }}
    >
      {(props.items || []).map(function (item, index) {
        return (
          <Box
            key={item.label + "-" + index}
            sx={{
              display: "flex",
              flex: "1 1 0",
              gap: "16px",
              alignItems: "flex-start",
              minWidth: 0,
            }}
          >
            <Typography
              sx={{
                fontFamily: JOINEE_FORM_FONT,
                fontSize: 14,
                fontWeight: 400,
                color: "#848F9A",
                lineHeight: 1.14,
                width: { xs: "140px", sm: "164px" },
                flexShrink: 0,
              }}
            >
              {item.label}
            </Typography>
            <Typography
              sx={{
                fontFamily: JOINEE_FORM_FONT,
                fontSize: 14,
                fontWeight: 400,
                color: "#171A1C",
                lineHeight: 1.14,
                minWidth: 0,
                wordBreak: "break-word",
              }}
            >
              {item.value}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}

function LegalBox(props) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        justifyContent: "center",
        alignItems: "flex-start",
        alignSelf: "stretch",
        width: "100%",
        minHeight: props.minHeight || 80,
        py: "10px",
        px: { xs: "12px", sm: "16px" },
        gap: "10px",
        bgcolor: "#F1F2F4",
        borderRadius: "4px",
        boxSizing: "border-box",
        mb: 3,
        mt: 1,
      }}
    >
      <Box
        component={props.asList ? "ol" : "div"}
        sx={{
          m: 0,
          width: "100%",
          pl: props.asList ? "20px" : 0,
          fontFamily: JOINEE_FORM_FONT,
          fontSize: 14,
          fontWeight: 400,
          color: "#2E3338",
          lineHeight: "22px",
          "& li": { mb: "8px" },
          "& li:last-child": { mb: 0 },
        }}
      >
        {props.children}
      </Box>
    </Box>
  );
}

function buildValidationSchema() {
  return Yup.object({
    EPF1952: Yup.string()
      .oneOf(["Yes", "No"], "Please select Yes or No")
      .required("Please select Yes or No"),
    EPF1955: Yup.string()
      .oneOf(["Yes", "No"], "Please select Yes or No")
      .required("Please select Yes or No")
      .test(
        "not-both-yes",
        "Both schemes cannot be Yes. Choose Yes/No, No/Yes, or No/No",
        function (value) {
          var epf1952 = this.parent && this.parent.EPF1952;
          if (epf1952 === "Yes" && value === "Yes") {
            return false;
          }
          return true;
        }
      ),
    passport: Yup.string()
      .oneOf(["Yes", "No"], "Please select Yes or No")
      .required("Please select Yes or No"),
    accountNumber: Yup.string()
      .trim()
      .required("Bank Account Number is required")
      .matches(ACCOUNT_REGEX, "Enter a valid account number (9–18 digits)"),
    IFSC: Yup.string()
      .trim()
      .required("IFSC Code is required")
      .test("ifsc-format", "Enter a valid IFSC code (e.g. SBIN0001234)", function (
        val
      ) {
        return IFSC_REGEX.test(normalizeIfsc(val));
      }),
    schemeCertificate: ruleSchema("longCode", {
      label: "Scheme Certificate No.",
    }),
    ppo: ruleSchema("longCode", { label: "PPO No." }),
    exitPreviousEmployment: Yup.mixed()
      .nullable()
      .test(
        "not-future-exit",
        "Exit date cannot be in the future",
        function (value) {
          if (!value) return true;
          return !isFutureDate(value);
        }
      )
      .test(
        "exit-before-doj",
        "Exit from previous employment must be on or before Date of Joining",
        function (value) {
          if (!value) return true;
          var doj = this.parent && this.parent.doj;
          if (!doj) return true;
          return !dayjs(value).isAfter(dayjs(doj), "day");
        }
      ),
    form11LegalConsent: Yup.boolean().oneOf(
      [true],
      "Please confirm that the information provided is true and accurate"
    ),
  });
}

function Form11(props) {
  var navigate = useNavigate();
  var params = useParams();
  var routeId = params.id ? params.id : "";
  var redux = useSelector(selectUser);
  var user = redux && redux.user ? redux.user : {};
  var isAdmin = Boolean(user.isAdmin);
  var userId = isAdmin ? props.id || routeId : user._id;

  var draftApi = useDraft("form11", "main");
  var loadDraft = draftApi.loadDraft;
  var saveDraft = draftApi.saveDraft;
  var deleteDraft = draftApi.deleteDraft;

  var [loading, setLoading] = useState(true);
  var [busy, setBusy] = useState(false);
  var [formKey, setFormKey] = useState(0);
  var [initialValues, setInitialValues] = useState(null);
  var [formFlagButton, setFormFlagButton] = useState("");
  var [approvedComment, setApprovedComment] = useState("");
  var [reSubmitComment, setReSubmitComment] = useState("");
  var [previewOpen, setPreviewOpen] = useState(false);
  var [previewHtml, setPreviewHtml] = useState("");
  var [previewLoading, setPreviewLoading] = useState(false);

  useEffect(
    function () {
      var isActive = true;
      if (!userId) {
        setLoading(false);
        return function () {
          isActive = false;
        };
      }

      setLoading(true);
      Form11API.getform11(userId)
        .then(function (res) {
          if (!isActive) {
            return;
          }
          var data = res && res.data ? res.data : {};
          var userResp = data.userResp || {};
          var form11Details = data.form11Details || {};
          var bank = data.bankDetailsResp || {};
          var medical = data.medicalInsuranceResp || {};
          var sdf = data.selfDeclarationResp || {};
          var relative = resolveRelative(userResp, form11Details);
          var todayIso = new Date().toISOString();
          var savedEpf1952 = form11Details.EPF1952;
          var savedEpf1955 = form11Details.EPF1955;
          // HR: No/No (fresher), Yes/No, No/Yes allowed; Yes/Yes not allowed
          var defaultEpf1952 = savedEpf1952 || "No";
          var defaultEpf1955 = savedEpf1955 || "No";
          if (!savedEpf1952 && !savedEpf1955) {
            defaultEpf1952 = "No";
            defaultEpf1955 = "No";
          } else if (
            savedEpf1952 === "Yes" &&
            savedEpf1955 === "Yes"
          ) {
            defaultEpf1952 = "Yes";
            defaultEpf1955 = "No";
          }

          var bankAccount =
            form11Details.accountNumber || bank.accountNumber || "";
          var bankIfsc = normalizeIfsc(
            form11Details.IFSC ||
              bank.ifscCode ||
              bank.IFSC ||
              bank.ifsc ||
              ""
          );

          var obj = {
            form11Id: form11Details._id || "",
            memberName: userResp.name || "",
            relationship: relative.relationship,
            relativeName: relative.relativeName,
            dob: medical.dob || userResp.dateOfBirth || "",
            gender: userResp.gender || "",
            maritalStatus: userResp.maritalStatus || "",
            emailId: userResp.email || "",
            phoneNumber: userResp.phoneNumber
              ? String(userResp.phoneNumber)
              : "",
            doj: userResp.doj || null,
            EPF1952: defaultEpf1952,
            EPF1955: defaultEpf1955,
            UAN: userResp.uan ? String(userResp.uan) : "",
            PFnumber: userResp.pf ? String(userResp.pf) : "",
            exitPreviousEmployment:
              form11Details.exitPreviousEmployment || "",
            schemeCertificate: form11Details.schemeCertificate || "",
            ppo: form11Details.ppo || "",
            passport: form11Details.passport || "No",
            accountNumber: digitsOnly(bankAccount).slice(0, 18),
            IFSC: bankIfsc,
            aadhar: userResp.aadhaar || userResp.aadhar || "",
            PAN: userResp.pan || "",
            authorization: {
              name: userResp.name || "",
              date: preferSavedDate(
                form11Details.authorization && form11Details.authorization.date,
                todayIso
              ),
              place: sdf.place || "",
            },
            father: safeFatherName(userResp),
            spouse: userResp.spouse || "",
            form11LegalConsent: Boolean(form11Details.form11LegalConsent),
            form11LegalConsentAt: form11Details.form11LegalConsentAt || null,
          };

          setFormFlagButton(userResp.isForm11Complete || "");
          setApprovedComment(
            userResp.internal_notes && userResp.internal_notes.form11Comment
              ? userResp.internal_notes.form11Comment
              : ""
          );
          setReSubmitComment(userResp.form11Comment || "");

          if (isAdmin) {
            setInitialValues(obj);
            setFormKey(function (k) {
              return k + 1;
            });
            setLoading(false);
            return;
          }

          return loadDraft().then(function (draft) {
            if (!isActive) {
              return;
            }
            var merged = applySectionDraftData("form11", "main", obj, draft);
            merged.memberName = userResp.name || "";
            merged.relativeName = relative.relativeName;
            merged.relationship = relative.relationship;
            merged.dob = obj.dob;
            merged.gender = obj.gender;
            merged.maritalStatus = obj.maritalStatus;
            merged.emailId = obj.emailId;
            merged.phoneNumber = obj.phoneNumber;
            merged.UAN = obj.UAN;
            merged.PFnumber = obj.PFnumber;
            merged.aadhar = obj.aadhar;
            merged.PAN = obj.PAN;
            if (!merged.accountNumber) {
              merged.accountNumber = obj.accountNumber;
            }
            if (!merged.IFSC) {
              merged.IFSC = obj.IFSC;
            } else {
              merged.IFSC = normalizeIfsc(merged.IFSC);
            }
            merged.authorization = {
              name: userResp.name || "",
              date: preferSavedDate(
                merged.authorization && merged.authorization.date
                  ? merged.authorization.date
                  : form11Details.authorization &&
                      form11Details.authorization.date,
                todayIso
              ),
              place: sdf.place || "",
            };
            merged.doj = obj.doj;
            if (!merged.EPF1952) merged.EPF1952 = obj.EPF1952;
            if (!merged.EPF1955) merged.EPF1955 = obj.EPF1955;
            if (!merged.passport) merged.passport = "No";
            setInitialValues(merged);
            setFormKey(function (k) {
              return k + 1;
            });
            setLoading(false);
          });
        })
        .catch(function (err) {
          console.log(err);
          if (isActive) {
            setLoading(false);
            toast.error("Unable to load Form 11");
          }
        });

      return function () {
        isActive = false;
      };
    },
    [userId, isAdmin, loadDraft]
  );

  var fieldsReadOnly =
    isAdmin || isJoineeEmployeeFormLocked(formFlagButton);
  var showCandidateActions =
    !isAdmin && !isJoineeEmployeeFormLocked(formFlagButton);

  function payloadFromValues(values) {
    var accepted = Boolean(values.form11LegalConsent);
    return {
      memberName: values.memberName || "",
      relationship: values.relationship || "father",
      relativeName: values.relativeName || "",
      maritalStatus: values.maritalStatus || "",
      emailId: values.emailId || "",
      phoneNumber: values.phoneNumber || "",
      EPF1952: values.EPF1952 || "No",
      EPF1955: values.EPF1955 || "No",
      exitPreviousEmployment: values.exitPreviousEmployment || "",
      schemeCertificate: values.schemeCertificate || "",
      ppo: values.ppo || "",
      passport: values.passport || "No",
      accountNumber: digitsOnly(values.accountNumber),
      IFSC: normalizeIfsc(values.IFSC),
      authorization: {
        name: values.memberName || "",
        date: preferSavedDate(
          values.authorization && values.authorization.date,
          new Date().toISOString()
        ),
        place:
          values.authorization && values.authorization.place
            ? values.authorization.place
            : "",
      },
      form11LegalConsent: accepted,
      form11LegalConsentAt: accepted
        ? values.form11LegalConsentAt || new Date().toISOString()
        : null,
    };
  }

  function persistForm11(values) {
    var payload = payloadFromValues(values);
    if (values.form11Id) {
      return Form11API.updateform11(userId, payload).then(function (res) {
        return { res: res, form11Id: values.form11Id };
      });
    }
    return Form11API.createform11(userId, payload).then(function (res) {
      var newId =
        res && res.data && res.data._id ? res.data._id : values.form11Id;
      return { res: res, form11Id: newId };
    });
  }

  function handleSaveDraft(formik) {
    if (isAdmin) {
      return Promise.resolve();
    }
    return validateDraft(formik, buildValidationSchema()).then(function (ok) {
      return ok ? persistDraft(formik) : null;
    });
  }

  function persistDraft(formik) {
    var values = formik.values;
    var wrapped = pickSectionDraftData("form11", "main", values);
    setBusy("draft");
    return saveDraft(wrapped)
      .then(function (saved) {
        setBusy(false);
        if (saved && saved.skipped) {
          toast.info("Nothing to save as draft");
          return saved;
        }
        if (saved) {
          toast.success("Draft saved");
          return saved;
        }
        toast.error("Unable to save draft");
        return null;
      })
      .catch(function () {
        setBusy(false);
        toast.error("Unable to save draft");
        return null;
      });
  }

  function handleSaveAndNext(values, formikHelpers) {
    if (isAdmin) {
      if (formikHelpers && formikHelpers.setSubmitting) {
        formikHelpers.setSubmitting(false);
      }
      return;
    }
    setBusy("next");
    persistForm11(values)
      .then(function (result) {
        if (result && result.form11Id && !values.form11Id) {
          formikHelpers.setFieldValue("form11Id", result.form11Id);
        }
        if (
          values.form11LegalConsent &&
          !values.form11LegalConsentAt &&
          formikHelpers.setFieldValue
        ) {
          formikHelpers.setFieldValue(
            "form11LegalConsentAt",
            payloadFromValues(values).form11LegalConsentAt
          );
        }
        return deleteDraft().then(function () {
          return Form11API.form11Status(userId);
        });
      })
      .then(function () {
        setBusy(false);
        if (formikHelpers && formikHelpers.setSubmitting) {
          formikHelpers.setSubmitting(false);
        }
        setFormFlagButton("submitted");
        toast.success("Form 11 submitted");
        navigate("/");
      })
      .catch(function (err) {
        console.log(err);
        setBusy(false);
        if (formikHelpers && formikHelpers.setSubmitting) {
          formikHelpers.setSubmitting(false);
        }
        toast.error("Unable to save Form 11");
      });
  }

  function openPreview() {
    if (!userId) {
      return;
    }
    setPreviewLoading(true);
    DownloadFormHtmlToPdf.getform(userId, "Form11FormHtmlData")
      .then(function (res) {
        setPreviewLoading(false);
        if (res && res.success) {
          setPreviewHtml(res.data || "");
          setPreviewOpen(true);
        } else {
          toast.error("Unable to load preview");
        }
      })
      .catch(function () {
        setPreviewLoading(false);
        toast.error("Unable to load preview");
      });
  }

  var formApprovedProps = {
    userId: props.id || routeId,
    formState: "isForm11Complete",
    nextURL: "AdminFormF",
    approvedComment: approvedComment,
    setApprovedComment: setApprovedComment,
  };

  var formReSubmitProps = {
    userId: props.id || routeId,
    formState: "isForm11Complete",
    nextURL: "AdminFormF",
    reSubmitComment: reSubmitComment,
    setReSubmitComment: setReSubmitComment,
  };

  if (loading || !initialValues) {
    return (
      <Box sx={{ width: "100%", py: 6, textAlign: "center" }}>
        <Typography
          sx={{
            fontFamily: JOINEE_FORM_FONT,
            fontSize: 14,
            color: JOINEE_FORM_COLORS.label,
          }}
        >
          Loading Form 11…
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", pb: 2 }}>
      {isAdmin && !props.hideHrActions && canShowHrReviewActions(formFlagButton) ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 1,
            mb: 1.5,
            width: { xs: "100%", md: "82%" },
            maxWidth: 780,
            mx: "auto",
          }}
        >
          {canShowHrApprove(formFlagButton) ? (
            <ApprovedCustomizedDialogs formApprovedProps={formApprovedProps} />
          ) : null}
          {canShowHrResubmit(formFlagButton) ? (
            <ReSubmitCustomizedDialogs formReSubmitProps={formReSubmitProps} />
          ) : null}
        </Box>
      ) : null}

      <Formik
        key={formKey}
        initialValues={initialValues}
        enableReinitialize
        validationSchema={buildValidationSchema()}
        onSubmit={handleSaveAndNext}
      >
        {function (formik) {
          var values = formik.values;

          return (
            <Form noValidate>
              <JoineeFormShell
                banner={
                  !isAdmin && formFlagButton === "resubmission required" ? (
                    <HrResubmitNotice comment={reSubmitComment} embedded />
                  ) : null
                }
                hideSteps
                onBack={
                  !isAdmin
                    ? function () {
                        navigate("/");
                      }
                    : null
                }
                title="EMPLOYEES' PROVIDENT FUND (PF) Form 11"
                subtitle="Provide your PF information to comply with statutory requirements and complete your employee records."
                headerAction={
                  isAdmin ? (
                    previewLoading ? (
                      <LoadingButtons />
                    ) : (
                      <Button
                        type="button"
                        variant="outlined"
                        onClick={openPreview}
                        sx={{
                          fontFamily: JOINEE_FORM_FONT,
                          fontSize: 14,
                          fontWeight: 400,
                          textTransform: "none",
                          color: JOINEE_FORM_COLORS.primary,
                          borderColor: JOINEE_FORM_COLORS.primary,
                          borderRadius: "6px",
                          minHeight: 32,
                          px: "12px",
                          bgcolor: "#FDFDFD",
                        }}
                      >
                        Preview Form
                      </Button>
                    )
                  ) : null
                }
                footer={
                  showCandidateActions ? (
                    <JoineeFormFooterActions
                      showPrevious
                      onPrevious={function () {
                        navigate("/");
                      }}
                      onDraft={function () {
                        return handleSaveDraft(formik);
                      }}
                      onNext={function () {
                        formik.validateForm().then(function (errors) {
                          if (errors && Object.keys(errors).length) {
                            notifyFormInvalid(errors);
                            formik.setTouched(
                              {
                                EPF1952: true,
                                EPF1955: true,
                                passport: true,
                                accountNumber: true,
                                IFSC: true,
                                form11LegalConsent: true,
                              },
                              true
                            );
                            return;
                          }
                          formik.handleSubmit();
                        });
                      }}
                      nextLabel="Save and next"
                      disabled={Boolean(busy)}
                      drafting={busy === "draft"}
                      saving={busy === "next"}
                    />
                  ) : null
                }
              >
                <FormSectionTitle>
                  EMPLOYEES&apos; PROVIDENT FUND ORGANISATION
                </FormSectionTitle>
                <LegalBox>
                  Employees&apos; Provident Fund Scheme, 1952 (Paragraph 34
                  &amp; 57) &amp; Employees&apos; Pension Scheme, 1995
                  (Paragraph 24)
                  <br />
                  (Declaration by a person taking up employment in any
                  establishment on which EPF Scheme, 1952 and EPS, 1995 is
                  applicable)
                </LegalBox>

                <ReadOnlyRow
                  items={[
                    {
                      label: "Full Name:",
                      value: displayValue(values.memberName),
                    },
                    {
                      label: "Father/Husband Name:",
                      value: displayValue(values.relativeName),
                    },
                  ]}
                />
                <ReadOnlyRow
                  items={[
                    {
                      label: "Date of Birth:",
                      value: formatDob(values.dob),
                    },
                    {
                      label: "Marital Status:",
                      value: formatMarital(values.maritalStatus),
                    },
                  ]}
                />
                <ReadOnlyRow
                  items={[
                    {
                      label: "Gender:",
                      value: formatGender(values.gender),
                    },
                    {
                      label: "Email ID:",
                      value: displayValue(values.emailId),
                    },
                  ]}
                />
                <ReadOnlyRow
                  items={[
                    {
                      label: "Phone Number:",
                      value: displayValue(values.phoneNumber),
                    },
                  ]}
                />

                <FormTwoCol>
                  <FormRadioField
                    name="EPF1952"
                    label="Whether earlier a member of EPF Scheme, 1952"
                    required
                    value={values.EPF1952}
                    options={YES_NO_OPTIONS}
                    onChange={function (e) {
                      var next = e.target.value;
                      formik.setFieldValue("EPF1952", next);
                      // Yes/Yes not allowed; No/No is allowed (fresher)
                      if (next === "Yes") {
                        formik.setFieldValue("EPF1955", "No");
                      }
                    }}
                    error={errMsg(formik, "EPF1952")}
                    disabled={fieldsReadOnly}
                  />
                  <FormRadioField
                    name="EPF1955"
                    label="Whether earlier a member of EPF Scheme, 1995"
                    required
                    value={values.EPF1955}
                    options={YES_NO_OPTIONS}
                    onChange={function (e) {
                      var next = e.target.value;
                      formik.setFieldValue("EPF1955", next);
                      if (next === "Yes") {
                        formik.setFieldValue("EPF1952", "No");
                      }
                    }}
                    error={errMsg(formik, "EPF1955")}
                    disabled={fieldsReadOnly}
                  />
                </FormTwoCol>

                <Box sx={{ mt: 3 }}>
                  <FormSectionTitle>Previous Employment Details</FormSectionTitle>
                </Box>
                <ReadOnlyRow
                  items={[
                    {
                      label: "UAN Number:",
                      value: displayValue(values.UAN),
                    },
                    {
                      label: "Previous PF Number:",
                      value: displayValue(values.PFnumber),
                    },
                  ]}
                />
                <FormTwoCol>
                  <FormDateField
                    id="form11-exit-date"
                    name="exitPreviousEmployment"
                    label="Date of exit Previous Employment"
                    placeholder="Select Date"
                    disableFuture
                    maxDate={values.doj || undefined}
                    value={values.exitPreviousEmployment}
                    onChange={function (iso) {
                      formik.setFieldValue("exitPreviousEmployment", iso);
                    }}
                    onBlur={formik.handleBlur}
                    error={errMsg(formik, "exitPreviousEmployment")}
                    disabled={fieldsReadOnly}
                  />
                  <FormTextField
                    id="form11-scheme-cert"
                    rule="longCode"
                    name="schemeCertificate"
                    label="Scheme Certificate No. (If issued)"
                    placeholder="e.g. MH/BAN/12345/2019"
                    value={values.schemeCertificate}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={errMsg(formik, "schemeCertificate")}
                    disabled={fieldsReadOnly}
                    inputProps={{ autoComplete: "off" }}
                  />
                </FormTwoCol>
                <FormTwoCol>
                  <FormTextField
                    id="form11-ppo"
                    rule="longCode"
                    name="ppo"
                    label="Pension Payment Order (PPO) No (If Issued)"
                    placeholder="e.g. MHBAN12345678901"
                    value={values.ppo}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={errMsg(formik, "ppo")}
                    disabled={fieldsReadOnly}
                    inputProps={{ autoComplete: "off" }}
                  />
                  <FormRadioField
                    name="passport"
                    label="International Worker"
                    required
                    value={values.passport}
                    options={YES_NO_OPTIONS}
                    onChange={function (e) {
                      formik.setFieldValue("passport", e.target.value);
                    }}
                    error={errMsg(formik, "passport")}
                    disabled={fieldsReadOnly}
                  />
                </FormTwoCol>

                <Box sx={{ mt: 3 }}>
                  <FormSectionTitle>
                    KYC Details (Attach self attested copies of following KYCs)
                  </FormSectionTitle>
                </Box>
                <FormTwoCol>
                  <FormTextField
                    id="form11-account"
                    name="accountNumber"
                    label="Bank Account Number"
                    placeholder="e.g. 123456789012"
                    hint="9 to 18 digits, as printed on your passbook or cheque."
                    required
                    value={values.accountNumber}
                    onChange={function (e) {
                      formik.setFieldValue(
                        "accountNumber",
                        digitsOnly(e.target.value).slice(0, 18)
                      );
                    }}
                    onBlur={formik.handleBlur}
                    error={errMsg(formik, "accountNumber")}
                    disabled={fieldsReadOnly}
                    inputProps={{
                      inputMode: "numeric",
                      maxLength: 18,
                      autoComplete: "off",
                    }}
                  />
                  <FormTextField
                    id="form11-ifsc"
                    name="IFSC"
                    label="IFSC Code"
                    placeholder="e.g. SBIN0001234"
                    hint="11 characters: 4 letters, then 0, then 6 letters or digits."
                    required
                    value={values.IFSC}
                    onChange={function (e) {
                      formik.setFieldValue(
                        "IFSC",
                        normalizeIfsc(e.target.value)
                      );
                    }}
                    onBlur={formik.handleBlur}
                    error={errMsg(formik, "IFSC")}
                    disabled={fieldsReadOnly}
                    inputProps={{
                      maxLength: 11,
                      autoComplete: "off",
                      style: { textTransform: "uppercase" },
                    }}
                  />
                </FormTwoCol>
                <ReadOnlyRow
                  items={[
                    {
                      label: "Aadhar Number:",
                      value: formatAadhaar(values.aadhar),
                    },
                    {
                      label: "PAN Number:",
                      value: displayValue(values.PAN),
                    },
                  ]}
                />

                <LegalBox minHeight={140}>
                  <Typography
                    sx={{
                      fontFamily: JOINEE_FORM_FONT,
                      fontSize: 14,
                      fontWeight: 700,
                      color: "#171A1C",
                      mb: 1,
                    }}
                  >
                    Undertaking
                  </Typography>
                  <Box
                    component="ol"
                    sx={{
                      m: 0,
                      pl: "20px",
                      fontFamily: JOINEE_FORM_FONT,
                      fontSize: 14,
                      fontWeight: 400,
                      color: "#2E3338",
                      lineHeight: "22px",
                      "& li": { mb: "8px" },
                      "& li:last-child": { mb: 0 },
                    }}
                  >
                    <li>
                      Certified that the particulars are true to the best of my
                      knowledge.
                    </li>
                    <li>
                      I authorize EPFO to use my Aadhaar for
                      verification/authentication/KYC purpose for service
                      delivery.
                    </li>
                    <li>
                      Kindly transfer the funds and service details, if
                      applicable, from the previous PF account as declared above
                      to the present PF Account. (The transfer would be possible
                      only if the identified KYC detail approved by previous
                      employer has been verified by present employer using his
                      Digital Signature Certificate)
                    </li>
                    <li>
                      In case of changes in above details, the same will be
                      intimated to employer at the earliest
                    </li>
                  </Box>
                </LegalBox>

                <FormSectionTitle>Authorization</FormSectionTitle>
                <ReadOnlyRow
                  items={[
                    {
                      label: "Full name:",
                      value: displayValue(
                        values.authorization
                          ? values.authorization.name
                          : values.memberName
                      ),
                    },
                    {
                      label: "Place:",
                      value: displayValue(
                        values.authorization
                          ? values.authorization.place
                          : ""
                      ),
                    },
                  ]}
                />
                <ReadOnlyRow
                  items={[
                    {
                      label: "Today’s Date:",
                      value: formatDob(
                        values.authorization
                          ? values.authorization.date
                          : null
                      ),
                    },
                  ]}
                />

                {!isAdmin ? (
                  <Box
                    sx={{ mt: 1, mb: 1 }}
                    data-ack-error={
                      errMsg(formik, "form11LegalConsent")
                        ? "true"
                        : undefined
                    }
                  >
                    <FormCheckboxField
                      name="form11LegalConsent"
                      checked={Boolean(values.form11LegalConsent)}
                      onChange={function (e) {
                        var checked = Boolean(e.target.checked);
                        formik.setFieldValue(
                          "form11LegalConsent",
                          checked,
                          true
                        );
                        formik.setFieldValue(
                          "form11LegalConsentAt",
                          checked
                            ? values.form11LegalConsentAt ||
                                new Date().toISOString()
                            : null,
                          false
                        );
                        formik.setFieldTouched(
                          "form11LegalConsent",
                          true,
                          false
                        );
                        if (checked) {
                          formik.setFieldError("form11LegalConsent", undefined);
                        }
                      }}
                      label="I confirm that the information provided is true and accurate, authorize the use of my previously submitted signature, and agree to all applicable terms and conditions."
                      disabled={fieldsReadOnly}
                      legal
                    />
                    {errMsg(formik, "form11LegalConsent") &&
                    !values.form11LegalConsent ? (
                      <Typography
                        sx={{
                          fontFamily: JOINEE_FORM_FONT,
                          fontSize: 12,
                          fontWeight: 400,
                          color: JOINEE_FORM_COLORS.error,
                          mt: 0.5,
                          ml: "28px",
                        }}
                      >
                        {errMsg(formik, "form11LegalConsent")}
                      </Typography>
                    ) : null}
                    <Box
                      sx={{
                        borderBottom:
                          "1px solid " + JOINEE_FORM_COLORS.border,
                        mt: 2,
                        mb: 1,
                      }}
                    />
                  </Box>
                ) : null}
              </JoineeFormShell>
            </Form>
          );
        }}
      </Formik>

      <Dialog
        open={previewOpen}
        onClose={function () {
          setPreviewOpen(false);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            fontFamily: JOINEE_FORM_FONT,
            fontWeight: 600,
            pr: 6,
          }}
        >
          Preview Form
          <IconButton
            aria-label="close"
            onClick={function () {
              setPreviewOpen(false);
            }}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box
            sx={{ width: "100%", overflowX: "auto" }}
            dangerouslySetInnerHTML={{ __html: previewHtml }}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default Form11;
